import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Loader2, ExternalLink, AlertTriangle, LogOut } from 'lucide-react';
import { allocateApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

/**
 * Allocate deep-link redirect handler.
 *
 * Two URL shapes are supported:
 *   • /external/allocate/:clientId/:allocateId   (recommended — Allocate emits this)
 *   • /external/allocate/:allocateId             (legacy — single-id, in-tenant only)
 *
 * Flows:
 *   200 → navigate to /assets?view=<asset_id> (drawer auto-opens)
 *   403 wrong_workspace → "you're signed in to a different workspace" page
 *                          with sign-out CTA so the user can switch.
 *   404 not_synced → "this asset hasn't been imported yet" CTA to /assets/import
 *   else → generic error card
 */
const ExternalAllocateRedirect = () => {
  const { clientId, allocateId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | not_found | wrong_workspace | error
  const [errorMessage, setErrorMessage] = useState('');
  const [expectedClientId, setExpectedClientId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const { data } = clientId
          ? await allocateApi.resolve(clientId, allocateId)
          : await allocateApi.resolve(allocateId);
        if (cancelled) return;
        navigate(`/assets?view=${data.asset_id}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const code = err.response?.data?.detail?.code;
        if (status === 403 && code === 'wrong_workspace') {
          setExpectedClientId(err.response?.data?.detail?.expected_client_id || null);
          setState('wrong_workspace');
        } else if (status === 404) {
          setState('not_found');
        } else {
          setErrorMessage(err.response?.data?.detail?.message || err.message || 'Unable to resolve');
          setState('error');
        }
      }
    };
    resolve();
  }, [clientId, allocateId, navigate]);

  if (state === 'loading') {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3" data-testid="allocate-redirect-loading">
        <Loader2 className="w-7 h-7 animate-spin text-[#FFD108]" />
        <p className="text-sm text-muted-foreground">
          Opening Allocate asset{' '}
          <span className="font-mono">
            {clientId ? `${clientId} · ` : ''}{allocateId}
          </span>…
        </p>
      </div>
    );
  }

  if (state === 'wrong_workspace') {
    const handleSwitch = () => {
      localStorage.removeItem('equipr_token');
      const next = clientId
        ? `/external/allocate/${clientId}/${allocateId}`
        : `/external/allocate/${allocateId}`;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    };
    return (
      <div className="max-w-2xl mx-auto py-12" data-testid="allocate-wrong-workspace">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-[#FFD108]" />
              Wrong workspace
            </CardTitle>
            <CardDescription>
              You're signed in to a different EQUIPR Guardian workspace.
              {expectedClientId && (
                <>
                  {' '}This asset lives under <span className="font-mono">{expectedClientId}</span>.
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Sign out and sign back in with the correct workspace to view this Allocate asset.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="allocate-stay-btn">
                Stay here
              </Button>
              <Button className="btn-volt" onClick={handleSwitch} data-testid="allocate-switch-workspace">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out & switch
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="max-w-2xl mx-auto py-12" data-testid="allocate-not-synced">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <ExternalLink className="w-6 h-6 text-[#FFD108]" />
              Not synced yet
            </CardTitle>
            <CardDescription>
              Allocate asset <span className="font-mono">{allocateId}</span>
              {clientId && (
                <>
                  {' '}for workspace <span className="font-mono">{clientId}</span>
                </>
              )}
              {' '}hasn't been imported to Guardian yet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              To make this link work, run an Allocate → Guardian import. Either export from Allocate using{' '}
              <code className="font-mono text-xs px-1 py-0.5 bg-muted/40 rounded">sp_Guardian_ExportAssets</code>{' '}
              and upload the CSV, or run a Live SQL import from Settings → Integrations.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/assets')} data-testid="allocate-back-btn">
                Back to assets
              </Button>
              <Button
                className="btn-volt"
                onClick={() => navigate('/assets/import')}
                data-testid="allocate-import-btn"
              >
                Import from Allocate now →
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              Couldn't open this asset
            </CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/assets')}>
              Back to assets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <Navigate to="/assets" replace />;
};

export default ExternalAllocateRedirect;
